import React, { useRef } from 'react';
import { ChevronDown } from 'lucide-react';

// Form controls for side panels. Labels sit above controls; hints and errors below.

export const controlClass = ({ invalid = false, mono = false } = {}) =>
  [
    'w-full h-8 px-2.5 rounded-control border bg-sheet text-ink outline-none focus:border-signal focus-visible:ring-2 focus-visible:ring-signal/40 disabled:opacity-60',
    invalid ? 'border-crit' : 'border-rule',
    mono ? 'font-mono text-id' : 'text-body',
  ].join(' ');

export function Field({ id, label, hint, error, children, className = '' }) {
  return (
    <div className={className}>
      <label htmlFor={id} className="block mb-1 text-small font-semibold text-ink">{label}</label>
      {children}
      {error ? (
        <p id={`${id}-error`} className="mt-1 text-small text-crit">{error}</p>
      ) : (
        hint && <p id={`${id}-hint`} className="mt-1 text-small text-graphite">{hint}</p>
      )}
    </div>
  );
}

// Native select with the shared control look. options: [{ value, label }]
export function Select({ options, className = '', ...rest }) {
  return (
    <div className="relative">
      <select {...rest} className={`${controlClass()} appearance-none pr-8 ${className}`}>
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
      <ChevronDown size={14} className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-graphite" aria-hidden="true" />
    </div>
  );
}

/**
 * One choice from a few short options, shown side by side.
 * options: [{ value, label }]. Arrow keys move the choice, as in a radio group.
 */
export function Segmented({ label, options, value, onChange, disabled = false }) {
  const refs = useRef([]);
  const index = options.findIndex((o) => o.value === value);
  const move = (step) => {
    const next = (index + step + options.length) % options.length;
    onChange(options[next].value);
    refs.current[next]?.focus();
  };

  return (
    <div role="radiogroup" aria-label={label} className="flex p-0.5 rounded-control border border-rule bg-paper">
      {options.map((option, i) => {
        const checked = option.value === value;
        return (
          <button
            key={option.value}
            ref={(el) => { refs.current[i] = el; }}
            type="button"
            role="radio"
            aria-checked={checked}
            tabIndex={checked ? 0 : -1}
            disabled={disabled}
            onClick={() => onChange(option.value)}
            onKeyDown={(event) => {
              if (event.key === 'ArrowRight' || event.key === 'ArrowDown') { event.preventDefault(); move(1); }
              if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') { event.preventDefault(); move(-1); }
            }}
            className={`flex-1 h-7 px-2 rounded-badge text-body disabled:opacity-60 ${checked ? 'bg-sheet text-ink font-semibold shadow-[0_0_0_1px_rgb(var(--rule))]' : 'text-graphite hover:text-ink'}`}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Radio buttons with a line of explanation under each label.
 * options: [{ value, label, description? }]
 */
export function RadioRows({ name, label, options, value, onChange, disabled = false }) {
  return (
    <fieldset disabled={disabled}>
      <legend className="mb-1.5 text-small font-semibold text-ink">{label}</legend>
      <div className="space-y-2">
        {options.map((option) => (
          <label key={option.value} className="flex items-start gap-2.5 cursor-pointer">
            <input
              type="radio"
              name={name}
              value={option.value}
              checked={option.value === value}
              onChange={() => onChange(option.value)}
              className="mt-0.5 h-4 w-4 shrink-0 accent-signal"
            />
            <span className="min-w-0">
              <span className="block text-body text-ink">{option.label}</span>
              {option.description && <span className="block text-small text-graphite">{option.description}</span>}
            </span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}
